import { useCallback } from 'react';

import { CircleHelpIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { PAUSE_DOCS_URL } from '@/util/constants';

import ToolkitWrapper from '../../ToolkitWrapper';

const DURATION_OPTIONS = Array.from({ length: 10 }, (_, i) => {
  const value = i + 1;
  return { value, label: value === 1 ? '1 second' : `${value} seconds` };
});

type FormType = { duration: string; show_typing: boolean };

const ToolkitActionPause = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { actionId, duration, show_typing } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as Extract<Action, { type: 'pause' }>;
    return {
      actionId: selectedAction.action_id,
      duration: selectedAction.duration,
      show_typing: selectedAction.show_typing,
    };
  }, shallowEqual);

  const form = useForm<FormType>({
    mode: 'onChange',
    defaultValues: { duration: duration.toString(), show_typing },
  });

  const onDurationChange = useCallback(
    (value: string) => {
      dispatch(updateAction({ actionId, action: { duration: parseInt(value, 10), show_typing } }));
    },
    [show_typing, dispatch, actionId]
  );

  const onTypingSwitch = useCallback(
    (newShowTyping: boolean) => {
      dispatch(updateAction({ actionId, action: { duration, show_typing: newShowTyping } }));
    },
    [dispatch, actionId, duration]
  );

  return (
    <ToolkitWrapper type="pause">
      <Form {...form}>
        <div className="flex flex-col gap-4 pb-6">
          <FormField
            control={form.control}
            name="duration"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.pause.duration')}
                  <InfoTooltip
                    content={t('dialog.pause.duration_help')}
                    learnMoreUrl={PAUSE_DOCS_URL}
                    size="large"
                  >
                    <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                  </InfoTooltip>
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    onDurationChange(value);
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('dialog.pause.select_a_duration')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {DURATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="show_typing"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between gap-4">
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.pause.show_typing_indicators')}
                  <InfoTooltip
                    content={t('dialog.pause.show_typing_help')}
                    learnMoreUrl={PAUSE_DOCS_URL}
                    size="large"
                  >
                    <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                  </InfoTooltip>
                </FormLabel>
                <Switch
                  checked={field.value}
                  onCheckedChange={(newValue) => {
                    field.onChange(newValue);
                    onTypingSwitch(newValue);
                  }}
                />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionPause;
